/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ChangePasswordDto,
    DeleteUserDto,
    EmailVerificationDto,
    EmailVerificationResponse,
    ForgetPasswordDto,
    InvitedUserSignupDto,
    LoginAccessTokenDto,
    PageDto,
    PageOptionsDto,
    RegisterUserDto,
    RegistrationResponse,
    ResetPasswordDto,
    SuccessDto,
    UpdateUserDto,
    UserDto,
    UserLoginDto
} from '@pragma/dto';
import { cloneDeep } from 'lodash';
import { ReplaySubject, catchError, finalize, of, switchMap } from 'rxjs';
import { Observable } from 'rxjs';
import { SKIP_ERROR_TOAST } from '../../interceptors/http-context-tokens';
import { HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { LocationDto } from '../../dto/src/locations/location-dto';
import { Role } from '../../interfaces/role.interface';

export enum UserPermission {
    Admin = '2',
    SiteManager = '4'
}

const httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private apiUrl = environment.API_ORIGIN;
    private _user: UserDto | undefined;
    private _userPopulated: ReplaySubject<boolean>;

    constructor(private readonly _httpClient: HttpClient) {
        this._userPopulated = new ReplaySubject<boolean>();

        if (localStorage.getItem('access_token') || sessionStorage.getItem('access_token')) {
            this.getUser().subscribe();
        } else {
            this._userPopulated.next(true);
        }
    }

    get user() {
        if (this._user) {
            return of(this._user);
        }

        return this._userPopulated.pipe(
            switchMap(() => {
                return of(this._user);
            })
        );
    }

    public getUser() {
        return this._httpClient
            .get<UserDto>('/users/me', { context: new HttpContext().set(SKIP_ERROR_TOAST, true) })
            .pipe(
                switchMap((user) => {
                    this._user = cloneDeep(user);
                    this._userPopulated.next(true);
                    return of(user);
                }),
                catchError((err) => {
                    console.log(err);
                    this._userPopulated.next(true);
                    return of(undefined);
                }),
                finalize(() => {
                    this._userPopulated.next(true);
                })
            );
    }

    public emptyUser() {
        this._user = undefined;
        this._userPopulated.next(false);
    }

    public getUsers() {
        return this._httpClient.get<Array<UserDto>>('/users');
    }

    public deleteUserAdmin(dto: DeleteUserDto) {
        return this._httpClient.delete<SuccessDto>('/user/admin-delete', { params: { userId: dto.userId } });
    }

    public deleteUser(dto: DeleteUserDto) {
        return this._httpClient.delete<SuccessDto>('/user', { params: { userId: dto.userId } });
    }

    public getPageUser(q: PageOptionsDto) {
        return this._httpClient.get<PageDto<UserDto>>('/users/page', {
            params: {
                page: q.page!,
                take: q.take!,
                order: q.order!,
                sortField: q.sortField!,
                filters: JSON.stringify(q.filters)
            }
        });
    }

    public login(email: string, password: string) {
        return this._httpClient
            .post<LoginAccessTokenDto>('/auth/signin', {
                email,
                password,
                internal: true
            })
            .pipe(
                switchMap((token) => {
                    return of(token);
                })
            );
    }

    public adminRegister(dto: RegisterUserDto) {
        return this._httpClient.post<LoginAccessTokenDto>('/user/admin-create', dto).pipe(
            switchMap((token) => {
                return of(token);
            })
        );
    }

    register(dto: InvitedUserSignupDto): Observable<RegistrationResponse> {
        return this._httpClient.post<RegistrationResponse>(`/users`, dto);
    }

    public forgetPassword(dto: ForgetPasswordDto) {
        return this._httpClient.post<SuccessDto>(
            '/auth/forget-password',
            { ...dto, internal: true },
            {
                context: new HttpContext().set(SKIP_ERROR_TOAST, true)
            }
        );
    }

    public getPasswordResetExpirationTime(userId: string) {
        return this._httpClient.get<SuccessDto>('/user/get-reset-password-time', { params: { userId } });
    }

    public logout() {
        this._user = undefined;
        this._userPopulated.next(false);
        localStorage.removeItem('access_token');
        sessionStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('refresh_token');
        //window.location.reload();
    }

    public refreshToken() {
        return this._httpClient.post<LoginAccessTokenDto>('/user/refresh-token', {}).pipe(
            switchMap((token) => {
                return of(token);
            })
        );
    }

    public loginViaUsernamePassword(body: UserLoginDto) {
        return this._httpClient.post<LoginAccessTokenDto>('/auth/signin', { ...body, internal: true }).pipe(
            switchMap((token) => {
                return of(token);
            })
        );
    }

    updateUser(userId: number, user: UpdateUserDto): Observable<SuccessDto> {
        return this._httpClient.put<SuccessDto>(`/users?id=${userId}`, user);
    }

    changePassword(user: ChangePasswordDto): Observable<SuccessDto> {
        return this._httpClient.post<SuccessDto>('/auth/change-password', { ...user, internal: true });
    }

    resetPassword(user: ResetPasswordDto) {
        return this._httpClient.post<SuccessDto>('/auth/reset-password', { ...user, internal: true });
    }

    verifyEmail(token: string) {
        const dto = new EmailVerificationDto();
        dto.token = token;
        return this._httpClient.put<EmailVerificationResponse>('/auth/verify-email', dto);
    }

    updateImage(form: FormData) {
        return this._httpClient.put<SuccessDto>('/user/change-image', form);
    }

    hasPermission(permission: UserPermission) {
        return true;
        // switch (permission) {
        //     case UserPermission.Admin:
        //         return this._user?.userType === UserType.ADMIN || this._user?.userType === UserType.SUPER_ADMIN;
        //     default:
        //         return false;
        // }
    }

    getRoles(): Observable<Array<Role>> {
        return this._httpClient.get<Array<Role>>('/roles/all');
    }

    public Update(id: number, data: UserDto): Observable<any> {
        var url = '/users?id=' + id;
        return this._httpClient.put(url, data, httpOptions).pipe(catchError(this.handleError('')));
    }

    public Post(data: any, url: string): Observable<any> {
        const return2 = this._httpClient.post(url, data, httpOptions).pipe(catchError(this.handleError('')));
        // return this._httpClient.post(url, data, httpOptions)
        //     .pipe(
        //         catchError(this.handleError(''))
        //     );
        return return2;
    }

    public Delete(id: number, url: string): Observable<any> {
        // var url = ${this.apiUrl}/users?=id=${id};
        var fullUrl = url + id;
        return this._httpClient.delete<any>(fullUrl, httpOptions);
    }

    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {
            // TODO: send the error to remote logging infrastructure

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }

    getToken() {
        return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    }

    public getUsersByPage(page: number) {
        return this._httpClient.get<Array<UserDto>>('/users/page?order=ASC&' + page + '=1&take=10');
    }

    public getUserById(id: number): Observable<UserDto> {
        const url = '/users/?id=' + id;
        return this._httpClient.get<UserDto>(url);
    }

    public getLocationbyUserId(id: number) {
        const url = '/users/user-locations?userId=' + id;
        return this._httpClient.get<LocationDto>(url);
    }
}
